/**
 * EmptyState (TASK-661) — the shared empty/feedback state for the product.
 *
 * Promoted from portfolio/atoms/EmptyState (which now re-exports this file) so
 * every surface uses one anatomy: icon, title, explanation, and — whenever a
 * valid next action exists — an action button. Tokenized colors; the action
 * uses the primary hierarchy.
 */

import React from 'react';
import { Database, FileX, Search, Users, TrendingUp, AlertCircle } from 'lucide-react';

export interface EmptyStateProps {
  type?: 'no-data' | 'no-results' | 'no-clients' | 'no-positions' | 'error' | 'custom';
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  type = 'no-data',
  title,
  description,
  icon,
  action,
  className = ''
}) => {
  const configs = {
    'no-data': {
      icon: Database,
      title: 'No Data Available',
      description: 'There is no data to display at this time.'
    },
    'no-results': {
      icon: Search,
      title: 'No Results Found',
      description: 'Try adjusting your search or filter criteria.'
    },
    'no-clients': {
      icon: Users,
      title: 'No Clients',
      description: 'No client portfolios found.'
    },
    'no-positions': {
      icon: TrendingUp,
      title: 'No Positions',
      description: 'This portfolio has no positions yet.'
    },
    'error': {
      icon: AlertCircle,
      title: 'Error Loading Data',
      description: 'There was an error loading the data. Please try again.'
    },
    'custom': {
      icon: FileX,
      title: title || 'No Items',
      description: description || 'No items to display.'
    }
  };

  const config = configs[type];
  const Icon = icon ? () => icon : config.icon;
  const displayTitle = title || config.title;
  const displayDescription = description || config.description;

  return (
    <div className={`flex flex-col items-center justify-center py-12 px-4 ${className}`}>
      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
        <Icon className="w-8 h-8 text-muted-foreground" />
      </div>

      <h3 className="text-lg font-semibold text-foreground mb-1">
        {displayTitle}
      </h3>

      <p className="text-sm text-muted-foreground text-center max-w-sm">
        {displayDescription}
      </p>

      {action && (
        <button
          onClick={action.onClick}
          className="mt-4 px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-md hover:bg-primary/90 transition-colors"
        >
          {action.label}
        </button>
      )}
    </div>
  );
};

export default EmptyState;
